import { useState, useEffect } from "react";
import PlanBuilder from "./PlanBuilder";
import MarketReport from "./MarketReport";
import PaymentLinkGenerator from "./PaymentLinkGenerator";

const SalesHub = ({ user, onLogout, onSwitchHub }) => {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("saleshub.tab") || "live"; } catch (e) { return "live"; }
  });
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // When the iframe opens the "Live Transactions" overlay we hide the tab-bar so it truly fills the screen.
  const [liveProofOpen, setLiveProofOpen] = useState(false);

  // Persist active tab so reps stay where they left off
  useEffect(() => {
    try { localStorage.setItem("saleshub.tab", tab); } catch (e) { /* ignore */ }
  }, [tab]);

  // Listen for postMessage from the Sales Hub iframe (logout / switch-hub / open plan builder / live-proof)
  useEffect(() => {
    const handler = (e) => {
      if (e.data === "switch-hub" && onSwitchHub) onSwitchHub();
      if (e.data === "logout") onLogout();
      if (e.data === "open-plan-builder") setTab("builder");
      if (e.data && e.data.type === "liveProofOpen") setLiveProofOpen(true);
      if (e.data && e.data.type === "liveProofClose") setLiveProofOpen(false);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onSwitchHub, onLogout]);

  // Send user data + role config to iframe after load
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    const iframe = document.querySelector('[data-testid="sales-hub-iframe"]');
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({
        type: "user-data",
        user: {
          user_id: user.user_id,
          name: user.name,
          email: user.email,
          department: user.department,
          role: user.role,
        },
        canSwitch: !!onSwitchHub,
      }, "*");
    }
  };

  const tabs = [
    { key: "live",    label: "Live Hub" },
    { key: "builder", label: "Personal Proposal Builder" },
    { key: "market",  label: "Market Report" },
    { key: "paylink", label: "Payment Link" },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: "#07070f" }} data-testid="sales-hub">
      {/* Top tab bar — hidden while the fullscreen "Live Transactions" overlay is open */}
      <div style={{ display: liveProofOpen ? "none" : "flex", alignItems: "center", gap: 4, padding: "10px 14px", background: "#050508", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
        <img src="/skyeai_head.png" alt="Skye AI" width={22} height={22} style={{ objectFit: "contain", marginRight: 6, filter: "drop-shadow(0 2px 6px rgba(244,114,182,0.4))" }} />
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`saleshub-tab-${t.key}`}
              style={{
                padding: "8px 18px",
                borderRadius: 999,
                border: active ? "1px solid rgba(220,50,140,0.55)" : "1px solid transparent",
                background: active ? "linear-gradient(135deg, rgba(220,50,140,0.22), rgba(157,50,243,0.16))" : "transparent",
                color: active ? "#fff" : "rgba(255,255,255,0.55)",
                fontSize: 13,
                fontWeight: 800,
                fontFamily: "'Manrope', system-ui, sans-serif",
                letterSpacing: "-0.005em",
                cursor: "pointer",
                transition: "all .18s",
              }}
            >
              {t.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {onSwitchHub && (
          <button
            onClick={onSwitchHub}
            data-testid="saleshub-switch-hub"
            style={{ padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
          >
            Switch hub
          </button>
        )}
        <button
          onClick={onLogout}
          data-testid="saleshub-logout"
          style={{ marginLeft: 8, padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
        >
          Log out
        </button>
      </div>

      {/* Tab content — keep both mounted so switching is instant, only display active */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {/* Live Hub — the iframe */}
        <div style={{ position: "absolute", inset: 0, display: tab === "live" ? "block" : "none" }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-[10000]">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#dc328c", borderTopColor: "transparent" }} />
              <p className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>Loading Sales Hub...</p>
            </div>
          )}
          <iframe
            src="/sales-hub.html"
            title="Skye AI Sales Hub"
            className="w-full h-full border-0"
            style={{ opacity: iframeLoaded ? 1 : 0, transition: "opacity 0.3s ease" }}
            onLoad={handleIframeLoad}
            data-testid="sales-hub-iframe"
          />
        </div>

        {/* Plan Builder — rendered directly (no iframe), only mounted when visible so it doesn't waste memory */}
        <div style={{ position: "absolute", inset: 0, overflow: "auto", display: tab === "builder" ? "block" : "none" }}>
          {tab === "builder" && (
            <PlanBuilder user={user} onLogout={onLogout} embedded />
          )}
        </div>

        {/* Market Report — new tab */}
        <div style={{ position: "absolute", inset: 0, overflow: "auto", display: tab === "market" ? "block" : "none" }}>
          {tab === "market" && <MarketReport />}
        </div>

        {/* Payment Link generator — rep-facing tool */}
        <div style={{ position: "absolute", inset: 0, overflow: "auto", display: tab === "paylink" ? "block" : "none" }}>
          {tab === "paylink" && <PaymentLinkGenerator />}
        </div>
      </div>
    </div>
  );
};

export default SalesHub;
